import React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, ModalButton, SIZE, ROLE } from 'baseui/modal';
import { KIND } from 'baseui/button';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  variant?: 'danger' | 'warning' | 'info';
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  variant = 'danger',
}) => {
  const getConfirmButtonColor = () => {
    switch (variant) {
      case 'danger':
        return {
          backgroundColor: '#DC2626',
          ':hover': { backgroundColor: '#B91C1C' },
        };
      case 'warning':
        return {
          backgroundColor: '#F59E0B',
          ':hover': { backgroundColor: '#D97706' },
        };
      case 'info':
        return {
          backgroundColor: 'var(--tenant-primary, #000)',
          ':hover': { backgroundColor: 'color-mix(in srgb, var(--tenant-primary, #000) 85%, black)' },
        };
      default:
        return {
          backgroundColor: '#DC2626',
          ':hover': { backgroundColor: '#B91C1C' },
        };
    }
  };

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      animate
      autoFocus
      size={SIZE.default}
      role={ROLE.dialog}
      closeable={!isLoading}
      overrides={{
        Root: {
          style: {
            zIndex: 10001,
          },
        },
        Dialog: {
          style: {
            borderRadius: '8px',
          },
        },
      }}
    >
      <ModalHeader>{title}</ModalHeader>
      <ModalBody>
        <div style={{ fontSize: '14px', color: '#374151', lineHeight: '1.5' }}>
          {message}
        </div>
      </ModalBody>
      <ModalFooter>
        <ModalButton
          kind={KIND.tertiary}
          onClick={onClose}
          disabled={isLoading}
        >
          {cancelText}
        </ModalButton>
        <ModalButton
          onClick={onConfirm}
          isLoading={isLoading}
          disabled={isLoading}
          overrides={{
            BaseButton: {
              style: getConfirmButtonColor(),
            },
          }}
        >
          {confirmText}
        </ModalButton>
      </ModalFooter>
    </Modal>
  );
};
